// The vocabulary of a service attempt and of intake, in one place.
//
// These lists are consumed by the admin attempt form, the client order form, the
// admin create-order form, the persistence mapper and the tests. Keeping them
// here is what stops the stored value drifting from the option that produced it
// — a dropdown whose values live inline in JSX is a dropdown nobody can test.
//
// Court-form wording is NOT here. Nothing in this file feeds a Judicial Council
// caption; those live in pos010Text / proofFields / proofPdf and stay verbatim.

// ── INTAKE: the person to be served ───────────────────────────────────────────
//
// The client's understanding of who they are pointing us at. Deliberately
// SEPARATE from the process server's later estimate of whoever opened the door
// (service_attempts.approximate_age): until service happens they are two
// different people, and collapsing them would let an intake guess masquerade as
// an observation in a declaration.
const SUBJECT_AGE_BANDS = [
  { value: "under_18", label: "Under 18" },
  { value: "18_25", label: "18–25" },
  { value: "26_35", label: "26–35" },
  { value: "36_45", label: "36–45" },
  { value: "46_55", label: "46–55" },
  { value: "56_65", label: "56–65" },
  { value: "66_plus", label: "66+" },
  { value: "unknown", label: "Unknown" },
];
const SUBJECT_AGE_VALUES = SUBJECT_AGE_BANDS.map((b) => b.value);
function subjectAgeLabel(value) {
  const hit = SUBJECT_AGE_BANDS.find((b) => b.value === value);
  return hit ? hit.label : null;
}
// Anything not on the list is discarded rather than stored — a value the form
// could not have produced is not a fact.
function normalizeSubjectAge(value) {
  const v = String(value == null ? "" : value).trim();
  return SUBJECT_AGE_VALUES.includes(v) ? v : null;
}

// ── ATTEMPT: who was contacted ────────────────────────────────────────────────
//
// ONE question, asked once. This replaces a pair of overlapping dropdowns that
// both recorded a relationship, so the same fact could be answered twice and
// disagree with itself.
const PERSON_PRESENT = [
  { value: "subject", label: "Person to Serve" },
  { value: "spouse", label: "Spouse" },
  { value: "family_member", label: "Family member" },
  { value: "roommate", label: "Roommate / resident" },
  { value: "neighbor", label: "Neighbor" },
  { value: "property_manager", label: "Property manager" },
  { value: "security", label: "Security / gate attendant" },
  { value: "coworker", label: "Employer / co-worker" },
  { value: "other", label: "Other" },
];
// Asked ONLY when "Family member" is chosen: "a family member answered" is not
// the same fact as "his mother answered", and a declaration may need the second.
const FAMILY_RELATIONS = [
  { value: "parent", label: "Parent" },
  { value: "adult_child", label: "Adult child" },
  { value: "sibling", label: "Sibling" },
  { value: "grandparent", label: "Grandparent" },
  { value: "other_relative", label: "Other relative" },
];

function needsFamilyRelation(personPresent) { return personPresent === "family_member"; }
function needsPersonOtherText(personPresent) { return personPresent === "other"; }

// ── ATTEMPT: physical description ─────────────────────────────────────────────
//
// A multi-select, because a person can wear glasses AND have a beard — the old
// single free-text box forced one string and was unusable as evidence. Gender,
// age, ethnicity, height, weight and hair colour remain their own fields.
const SPECIAL_FEATURES = [
  { value: "glasses", label: "Glasses" },
  { value: "sunglasses", label: "Sunglasses" },
  { value: "beard", label: "Beard" },
  { value: "mustache", label: "Mustache / goatee" },
  { value: "goatee", label: "Goatee" },
  { value: "accent", label: "Accent" },
  { value: "tattoos", label: "Visible tattoos" },
  { value: "scars", label: "Scars" },
  { value: "piercings", label: "Piercings" },
  { value: "birthmark", label: "Birthmark" },
  { value: "wheelchair", label: "Wheelchair / mobility aid" },
  { value: "cane", label: "Cane / walker" },
  { value: "hearing_aid", label: "Hearing aid" },
  { value: "uniform", label: "Uniform / work attire" },
  { value: "head_covering", label: "Head covering" },
  { value: "none_visible", label: "None visible" },
  { value: "other", label: "Other" },
];
const SPECIAL_FEATURE_VALUES = SPECIAL_FEATURES.map((f) => f.value);

function needsFeatureOtherText(features) {
  return Array.isArray(features) && features.includes("other");
}
// Keeps only recognised values, in list order, without duplicates.
function normalizeFeatures(features) {
  const chosen = Array.isArray(features) ? features : [];
  const out = SPECIAL_FEATURE_VALUES.filter((v) => chosen.includes(v));
  return out.length ? out : null;
}
function featureLabels(features) {
  return (Array.isArray(features) ? features : [])
    .map((v) => (SPECIAL_FEATURES.find((f) => f.value === v) || {}).label)
    .filter(Boolean);
}

// ── ATTEMPT: which questions belong on this form ──────────────────────────────
//
// A successful personal service should not be interrogated about who ELSE was
// contacted or what the neighbours said — those questions exist to document a
// FAILED attempt. Asking them universally produced forms full of blank fields
// that read, in a declaration, as facts nobody recorded.
//
//   served      the person to serve took the papers
//   refused     they were found and would not take them
//   unsuccessful nobody was served this visit
function attemptKind(form) {
  const f = form || {};
  if (f.status === "served") return "served";
  if (f.serviceAccepted === "refused") return "refused";
  return "unsuccessful";
}

function attemptSections(form) {
  const kind = attemptKind(form);
  const failed = kind !== "served";
  return {
    kind,
    // Who answered: always relevant.
    personPresent: true,
    // What they said and what the address looked like document a failure.
    statement: failed,
    observations: failed,
    // "Did you speak to anyone else?" only makes sense if the subject was not served.
    otherContact: failed,
    // A description is worth recording whenever a person was actually contacted.
    description: kind !== "unsuccessful" || Boolean((form || {}).personPresent && form.personPresent !== "nobody"),
    // Mailing follow-up belongs to substituted service.
    mailing: (form || {}).serviceType === "substitute",
  };
}

// On a successful personal service the person contacted IS the person to serve.
// Defaulted rather than demanded, and only when the field is still empty, so an
// admin correcting it is never overwritten.
function defaultsForKind(form) {
  const f = form || {};
  if (attemptKind(f) === "served" && f.serviceType !== "substitute" && !f.personPresent) {
    return { personPresent: "subject" };
  }
  return {};
}

module.exports = {
  SUBJECT_AGE_BANDS, SUBJECT_AGE_VALUES, subjectAgeLabel, normalizeSubjectAge,
  PERSON_PRESENT, FAMILY_RELATIONS, needsFamilyRelation, needsPersonOtherText,
  SPECIAL_FEATURES, SPECIAL_FEATURE_VALUES, needsFeatureOtherText, normalizeFeatures, featureLabels,
  attemptKind, attemptSections, defaultsForKind,
};
